import React, { useRef, useState } from "react";

export function ShareButton({
    url = typeof window !== "undefined" ? window.location.href : "",
    title = "Check this out!",
    text = "Look at this amazing profile!",
}) {

    const dialogRef = useRef(null);
    const [copyStatus, setCopyStatus] = useState("");

    const shareData = { title, text, url };

    const handleShareClick = async () => {
        if (navigator?.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch { }
        }
        dialogRef.current.showModal();
    };


    function legacyCopyToClipboard(text) {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;

            // Avoid scrolling on iOS
            textArea.style.position = "fixed";
            textArea.style.top = "0";
            textArea.style.left = "0";
            textArea.style.opacity = "0";

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand("copy");
            document.body.removeChild(textArea);

            return successful;
        } catch (err) {
            console.error("Legacy copy failed", err);
            return false;
        }
    }

    const handleCopy = async () => {
        let success = false;

        // Modern API (supported on many desktop + some mobile browsers)
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                success = true;
            } catch (err) {
                console.warn("navigator.clipboard.writeText failed, falling back", err);
            }
        }

        // Fallback for older / mobile browsers (iOS Safari, in-app WebViews, etc.)
        if (!success) {
            success = legacyCopyToClipboard(url);
        }

        if (success) {
            setCopyStatus("Copied!");
        } else {
            setCopyStatus("Failed");
        }

        setTimeout(() => setCopyStatus(""), 3000);
    };


    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    const encodedTitle = encodeURIComponent(title);

    /* === REAL BRAND SHARE LINKS === */
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const links = {
        whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedText}%20${encodedUrl}`,

        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,

        x: isMobile
            ? `twitter://post?message=${encodedTitle}%20${encodedText}%20${encodedUrl}`   // try open app
            : `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,  // fallback

        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,

        telegram: isMobile
            ? `tg://msg?text=${encodedTitle}%20${encodedText}%20${encodedUrl}`   // mobile app
            : `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,

        reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,

        pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`,

        messenger: isMobile
            ? `fb-messenger://share/?link=${encodedUrl}`
            : `https://www.facebook.com/dialog/send?link=${encodedUrl}`,

        email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}%0A${encodedUrl}`,

        instagram: `https://www.instagram.com/`,  // best possible on web
    };



    /* === OFFICIAL BRAND SVG ICONS === */
    const icons = {

        whatsapp: (
            <svg
                className="w-7 h-7"
                viewBox="0 0 24 24"
                aria-hidden="true"
                fill="white"
            >
                <path d="M16.6,14c-0.2-0.1-1.5-0.7-1.7-0.8c-0.2-0.1-0.4-0.1-0.6,0.1c-0.2,0.2-0.6,0.8-0.8,1c-0.1,0.2-0.3,0.2-0.5,0.1c-0.7-0.3-1.4-0.7-2-1.2c-0.5-0.5-1-1.1-1.4-1.7c-0.1-0.2,0-0.4,0.1-0.5c0.1-0.1,0.2-0.3,0.4-0.4c0.1-0.1,0.2-0.3,0.2-0.4c0.1-0.1,0.1-0.3,0-0.4c-0.1-0.1-0.6-1.3-0.8-1.8C9.4,7.3,9.2,7.3,9,7.3c-0.1,0-0.3,0-0.5,0C8.3,7.3,8,7.5,7.9,7.6C7.3,8.2,7,8.9,7,9.7c0.1,0.9,0.4,1.8,1,2.6c1.1,1.6,2.5,2.9,4.2,3.7c0.5,0.2,0.9,0.4,1.4,0.5c0.5,0.2,1,0.2,1.6,0.1c0.7-0.1,1.3-0.6,1.7-1.2c0.2-0.4,0.2-0.8,0.1-1.2C17,14.2,16.8,14.1,16.6,14 M19.1,4.9C15.2,1,8.9,1,5,4.9c-3.2,3.2-3.8,8.1-1.6,12L2,22l5.3-1.4c1.5,0.8,3.1,1.2,4.7,1.2h0c5.5,0,9.9-4.4,9.9-9.9C22,9.3,20.9,6.8,19.1,4.9 M16.4,18.9c-1.3,0.8-2.8,1.3-4.4,1.3h0c-1.5,0-2.9-0.4-4.2-1.1l-0.3-0.2l-3.1,0.8l0.8-3l-0.2-0.3C2.6,12.4,3.8,7.4,7.7,4.9S16.6,3.7,19,7.5C21.4,11.4,20.3,16.5,16.4,18.9" />
            </svg>
        ),

        facebook: (
            <svg className="w-6 h-6" fill="white" viewBox="0 0 32 32">
                <path d="M19 6h5V0h-5c-5.5 0-9 3.6-9 9v4H5v6h5v13h6V19h5l1-6h-6V9c0-1.7 1.3-3 3-3z" />
            </svg>
        ),

        x: (
            <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                <path d="M18.9 1.2h3.6l-7.9 9.1 9.3 12.5h-7.3l-5.7-7.5-6.5 7.5H1.8l8.4-9.7L1.1 1.2h7.5l5.2 6.9z" />
            </svg>
        ),

        linkedin: (
            <svg className="w-6 h-6" fill="white" viewBox="0 0 32 32">
                <path d="M29 0H3C1.3 0 0 1.3 0 3v26c0 1.7 1.3 3 3 3h26c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3zM10 27H5V12h5v15zM7.5 10.2c-1.6 0-2.7-1.2-2.7-2.6s1.1-2.6 2.8-2.6c1.7 0 2.7 1.2 2.7 2.6-.1 1.5-1.1 2.6-2.8 2.6zM27 27h-5V19c0-1.9-.7-3.2-2.3-3.2-1.3 0-2 1-2.3 1.9-.1.3-.1.7-.1 1.1V27h-5V12h5v2.1c.7-1.1 1.8-2.6 4.4-2.6 3.2 0 5.6 2.1 5.6 6.6V27z" />
            </svg>
        ),

        telegram: (
            <svg
                className="w-7 h-7"
                viewBox="0 0 48 48"
                aria-hidden="true"
                fill='#FFFFFF'
            >
                <path d="M41.4193 7.30899C41.4193 7.30899 45.3046 5.79399 44.9808 9.47328C44.8729 10.9883 43.9016 16.2908 43.1461 22.0262L40.5559 39.0159C40.5559 39.0159 40.3401 41.5048 38.3974 41.9377C36.4547 42.3705 33.5408 40.4227 33.0011 39.9898C32.5694 39.6652 24.9068 34.7955 22.2086 32.4148C21.4531 31.7655 20.5897 30.4669 22.3165 28.9519L33.6487 18.1305C34.9438 16.8319 36.2389 13.8019 30.8426 17.4812L15.7331 27.7616C15.7331 27.7616 14.0063 28.8437 10.7686 27.8698L3.75342 25.7055C3.75342 25.7055 1.16321 24.0823 5.58815 22.459C16.3807 17.3729 29.6555 12.1786 41.4193 7.30899Z" />
            </svg>
        ),

        reddit: (
            <svg
                className="w-7 h-7"
                viewBox="0 0 216 216"
                xmlSpace="preserve"
                xmlnsXlink="http://www.w3.org/1999/xlink"
            >
                <defs>
                    <radialGradient
                        id="snoo-radial-gradient"
                        cx="169.75"
                        cy="92.19"
                        r="50.98"
                        fx="169.75"
                        fy="92.19"
                        gradientTransform="matrix(1 0 0 .87 0 11.64)"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#feffff" />
                        <stop offset=".4" stopColor="#feffff" />
                        <stop offset=".51" stopColor="#f9fcfc" />
                        <stop offset=".62" stopColor="#edf3f5" />
                        <stop offset=".7" stopColor="#dee9ec" />
                        <stop offset=".72" stopColor="#d8e4e8" />
                        <stop offset=".76" stopColor="#ccd8df" />
                        <stop offset=".8" stopColor="#c8d5dd" />
                        <stop offset=".83" stopColor="#ccd6de" />
                        <stop offset=".85" stopColor="#d8dbe2" />
                        <stop offset=".88" stopColor="#ede3e9" />
                        <stop offset=".9" stopColor="#ffebef" />
                    </radialGradient>

                    <radialGradient
                        id="snoo-radial-gradient-2"
                        xlinkHref="#snoo-radial-gradient"
                        cx="47.31"
                        r="50.98"
                        fx="47.31"
                    />

                    <radialGradient
                        id="snoo-radial-gradient-3"
                        xlinkHref="#snoo-radial-gradient"
                        cx="109.61"
                        cy="85.59"
                        r="153.78"
                        fx="109.61"
                        fy="85.59"
                        gradientTransform="matrix(1 0 0 .7 0 25.56)"
                    />

                    <radialGradient
                        id="snoo-radial-gradient-4"
                        cx="-6.01"
                        cy="64.68"
                        r="12.85"
                        fx="-6.01"
                        fy="64.68"
                        gradientTransform="matrix(1.07 0 0 1.55 81.08 27.26)"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#f60" />
                        <stop offset=".5" stopColor="#ff4500" />
                        <stop offset=".7" stopColor="#fc4301" />
                        <stop offset=".82" stopColor="#f43f07" />
                        <stop offset=".92" stopColor="#e53812" />
                        <stop offset="1" stopColor="#d4301f" />
                    </radialGradient>

                    <radialGradient
                        id="snoo-radial-gradient-5"
                        xlinkHref="#snoo-radial-gradient-4"
                        cx="-73.55"
                        cy="64.68"
                        r="12.85"
                        fx="-73.55"
                        fy="64.68"
                        gradientTransform="matrix(-1.07 0 0 1.55 62.87 27.26)"
                    />

                    <radialGradient
                        id="snoo-radial-gradient-6"
                        cx="107.93"
                        cy="166.96"
                        r="45.3"
                        fx="107.93"
                        fy="166.96"
                        gradientTransform="matrix(1 0 0 .66 0 57.4)"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#172e35" />
                        <stop offset=".29" stopColor="#0e1c21" />
                        <stop offset=".73" stopColor="#030708" />
                        <stop offset="1" stopColor="#000" />
                    </radialGradient>

                    <radialGradient
                        id="snoo-radial-gradient-7"
                        xlinkHref="#snoo-radial-gradient"
                        cx="147.88"
                        cy="32.94"
                        r="39.77"
                        fx="147.88"
                        fy="32.94"
                        gradientTransform="matrix(1 0 0 .98 0 .54)"
                    />

                    <radialGradient
                        id="snoo-radial-gradient-8"
                        cx="131.31"
                        cy="73.08"
                        r="32.6"
                        fx="131.31"
                        fy="73.08"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset=".48" stopColor="#7a9299" />
                        <stop offset=".67" stopColor="#172e35" />
                        <stop offset=".75" stopColor="#000" />
                        <stop offset=".82" stopColor="#172e35" />
                    </radialGradient>
                </defs>

                <path d="M108 0C48.35 0 0 48.35 0 108c0 29.82 12.09 56.82 31.63 76.37l-20.57 20.57C6.98 209.02 9.87 216 15.64 216H108c59.65 0 108-48.35 108-108S167.65 0 108 0Z" fill="#ff4500" />

                <circle cx="169.22" cy="106.98" r="25.22" fill="url(#snoo-radial-gradient)" />
                <circle cx="46.78" cy="106.98" r="25.22" fill="url(#snoo-radial-gradient-2)" />

                <ellipse cx="108.06" cy="128.64" rx="72" ry="54" fill="url(#snoo-radial-gradient-3)" />

                <path d="M86.78 123.48c-.42 9.08-6.49 12.38-13.56 12.38s-12.46-4.93-12.04-14.01c.42-9.08 6.49-15.02 13.56-15.02s12.46 7.58 12.04 16.66Z"
                    fill="url(#snoo-radial-gradient-4)"
                />

                <path d="M129.35 123.48c.42 9.08 6.49 12.38 13.56 12.38s12.46-4.93 12.04-14.01c-.42-9.08-6.49-15.02-13.56-15.02s-12.46 7.58-12.04 16.66Z"
                    fill="url(#snoo-radial-gradient-5)"
                />

                <ellipse cx="79.63" cy="116.37" className="snoo-cls-11" rx="2.8" ry="3.05" fill="#ffc49c" />
                <ellipse cx="146.21" cy="116.37" className="snoo-cls-11" rx="2.8" ry="3.05" fill="#ffc49c" />

                <path d="M108.06 142.92c-8.76 0-17.16.43-24.92 1.22-1.33.13-2.17 1.51-1.65 2.74 4.35 10.39 14.61 17.69 26.57 17.69s22.23-7.3 26.57-17.69c.52-1.23-.33-2.61-1.65-2.74-7.77-.79-16.16-1.22-24.92-1.22Z"
                    fill="url(#snoo-radial-gradient-6)"
                />

                <circle cx="147.49" cy="49.43" r="17.87" fill="url(#snoo-radial-gradient-7)" />

                <path d="M107.8 76.92c-2.14 0-3.87-.89-3.87-2.27 0-16.01 13.03-29.04 29.04-29.04 2.14 0 3.87 1.73 3.87 3.87s-1.73 3.87-3.87 3.87c-11.74 0-21.29 9.55-21.29 21.29 0 1.38-1.73 2.27-3.87 2.27Z"
                    fill="url(#snoo-radial-gradient-8)"
                />

                <path
                    d="M62.82 122.65c.39-8.56 6.08-14.16 12.69-14.16 6.26 0 11.1 6.39 11.28 14.33.17-8.88-5.13-15.99-12.05-15.99s-13.14 6.05-13.56 15.2c-.42 9.15 4.97 13.83 12.04 13.83h.52c-6.44-.16-11.3-4.79-10.91-13.2ZM153.3 122.65c-.39-8.56-6.08-14.16-12.69-14.16-6.26 0-11.1 6.39-11.28 14.33-.17-8.88 5.13-15.99 12.05-15.99 7.07 0 13.14 6.05 13.56 15.2.42 9.15-4.97 13.83-12.04 13.83h-.52c6.44-.16 11.3-4.79 10.91-13.2Z"
                    fill="#842123"
                />
            </svg>
        ),

        pinterest: (
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="white">
                <path d="M12.04.3C5.73.3.6 5.43.6 11.74c0 4.7 2.9 8.73 7.1 10.35-.1-.88-.2-2.23 0-3.19.2-.88 1.39-5.9 1.39-5.9s-.35-.71-.35-1.77c0-1.66.97-2.9 2.18-2.9 1.03 0 1.53.77 1.53 1.7 0 1.03-.65 2.57-.98 4-.28 1.19.6 2.17 1.78 2.17 2.14 0 3.78-2.26 3.78-5.53 0-2.88-2.07-4.88-5.02-4.88-3.42 0-5.43 2.56-5.43 5.2 0 1.03.4 2.14.92 2.73.1.11.12.2.09.31-.09.34-.3 1.09-.34 1.25-.05.17-.18.23-.41.14-1.52-.7-2.48-2.89-2.48-4.65C4.12 6.48 6.87 3 12.04 3c4.15 0 7.38 2.96 7.38 6.91 0 4.12-2.6 7.44-6.2 7.44-1.22 0-2.37-.64-2.76-1.39l-.75 2.86c-.27 1.04-1.01 2.34-1.51 3.13A11.4 11.4 0 0 0 12.04 24c6.31 0 11.44-5.13 11.44-11.44C23.48 5.43 18.35.3 12.04.3z" />
            </svg>
        ),

        messenger: (
            <svg className="w-7 h-7" fill="white" viewBox="0 0 24 24" id="meteor-icon-kit__regular-messanger" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 11.6407C0 4.9526 5.23944 0.000488281 12 0.000488281C18.7606 0.000488281 24 4.9526 24 11.6407C24 18.3289 18.7606 23.281 12 23.281C10.7855 23.281 9.61932 23.1216 8.52555 22.8198C8.31308 22.7619 8.08853 22.7788 7.88571 22.8681L5.50503 23.9184C4.88209 24.1912 4.17948 23.7494 4.15775 23.0685L4.09256 20.9341C4.0829 20.6709 3.96459 20.427 3.76901 20.2508C1.43421 18.1623 0 15.1393 0 11.6407ZM8.32032 9.45321L4.79517 15.0452C4.45473 15.5812 5.1163 16.1872 5.62093 15.8033L9.40684 12.9301C9.66278 12.7369 10.0153 12.7345 10.2736 12.9277L13.0769 15.0307C13.9171 15.6609 15.1195 15.4387 15.6797 14.5502L19.2048 8.95823C19.5453 8.42222 18.8837 7.81618 18.3791 8.20009L14.5932 11.0733C14.3372 11.2665 13.9847 11.2689 13.7264 11.0757L10.9231 8.97272C10.0829 8.34254 8.88048 8.56467 8.32032 9.45321Z" fill="#0866FF" />
            </svg>
        ),

        email: (
            <svg
                className="w-8 h-8"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M22.0515 8.52295L16.0644 13.1954L9.94043 8.52295V8.52421L9.94783 8.53053V15.0732L15.9954 19.8466L22.0515 15.2575V8.52295Z" fill="#EA4335" />
                <path d="M23.6231 7.38639L22.0508 8.52292V15.2575L26.9983 11.459V9.17074C26.9983 9.17074 26.3978 5.90258 23.6231 7.38639Z" fill="#FBBC05" />
                <path d="M22.0508 15.2575V23.9924H25.8428C25.8428 23.9924 26.9219 23.8813 26.9995 22.6513V11.459L22.0508 15.2575Z" fill="#34A853" />
                <path d="M9.94811 24.0001V15.0732L9.94043 15.0669L9.94811 24.0001Z" fill="#C5221F" />
                <path d="M9.94014 8.52404L8.37646 7.39382C5.60179 5.91001 5 9.17692 5 9.17692V11.4651L9.94014 15.0667V8.52404Z" fill="#C5221F" />
                <path d="M9.94043 8.52441V15.0671L9.94811 15.0734V8.53073L9.94043 8.52441Z" fill="#C5221F" />
                <path d="M5 11.4668V22.6591C5.07646 23.8904 6.15673 24.0003 6.15673 24.0003H9.94877L9.94014 15.0671L5 11.4668Z" fill="#4285F4" />
            </svg>
        ),

        instagram: (
            <svg
                className="w-7 h-7"
                viewBox="0 0 32 32"
                aria-hidden="true"
            >
                <path
                    d="M16 10.2A5.8 5.8 0 1 0 21.8 16 5.8 5.8 0 0 0 16 10.2Zm0 9.5A3.7 3.7 0 1 1 19.7 16 3.7 3.7 0 0 1 16 19.7Zm7.4-9.9a1.4 1.4 0 1 1-1.4-1.4 1.4 1.4 0 0 1 1.4 1.4Z"
                    fill="#fff"
                />
                <path
                    d="M22 4H10A6 6 0 0 0 4 10v12a6 6 0 0 0 6 6h12a6 6 0 0 0 6-6V10a6 6 0 0 0-6-6Zm4 18a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V10a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4Z"
                    fill="#fff"
                />
            </svg>
        ),
    };

    const items = [
        { name: "WhatsApp", color: "#25D366", icon: icons.whatsapp, link: links.whatsapp },
        { name: "Facebook", color: "#1877F2", icon: icons.facebook, link: links.facebook },
        { name: "X", color: "#000000", icon: icons.x, link: links.x },
        { name: "LinkedIn", color: "#0077B5", icon: icons.linkedin, link: links.linkedin },
        { name: "Telegram", color: "#229ED9", icon: icons.telegram, link: links.telegram },
        { name: "Reddit", color: "#FFFFFF", icon: icons.reddit, link: links.reddit },
        { name: "Pinterest", color: "#BD081C", icon: icons.pinterest, link: links.pinterest },
        { name: "Messenger", color: "#FFFFFF", icon: icons.messenger, link: links.messenger },
        { name: "Gmail", color: "#FFFFFF", icon: icons.email, link: links.email },
        {
            name: "Instagram",
            color: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)",
            icon: icons.instagram,
            link: links.instagram,
        },
    ];

    const handleClickOutside = (e) => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        // If clicked exact backdrop area
        if (e.target === dialog) {
            dialog.close();
        }
    };

    return (
        <>
            {/* Trigger */}
            <button
                onClick={handleShareClick}
                className="flex items-center bg-green-500 text-white text-sm text-center justify-center px-3 py-1 rounded-xl rounded-tr-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 font-urbanist hover:scale-105 transition"
            >
                <span className="hidden md:block mr-1 font-semibold">Share</span>
                <img className="h-4 w-4" src="/assets/individual/Share.png" alt='share-profile' />
            </button>


            {/* DIALOG */}
            <dialog
                ref={dialogRef}
                onClick={handleClickOutside}
                className="rounded-2xl w-96 p-6 bg-[#22262E] backdrop:bg-black/50 backdrop:backdrop-blur-sm"
            >
                <button
                    onClick={() => dialogRef.current.close()}
                    className="absolute top-3 right-4 p-1.5 rounded-full text-white hover:bg-gray-800"
                >
                    ✕
                </button>

                <h2 className="text-lg text-white font-semibold text-center mb-4">
                    Share this profile
                </h2>

                {/* Copy Link */}
                <div className="w-full flex justify-center mb-4">
                    <div
                        className="flex-1 min-w-0 text-xs border px-2 py-1 rounded-l-md
               bg-[#22262E] text-gray-400 border-gray-700
               whitespace-normal break-words"
                    >
                        {url}
                    </div>
                    <button
                        onClick={handleCopy}
                        className="flex items-center px-3 py-1 text-xs bg-gray-200 rounded-r-md gap-1"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                            <g id="🔍-Product-Icons" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                <g id="ic_fluent_copy_link_24_filled" fill="#212121" fillRule="nonzero">
                                    <path d="M13.7533481,6.49330383 L10.2466519,6.49330383 C9.46988587,6.49330383 8.78519098,6.09910034 8.38170952,5.49983563 L6.25,5.5 C5.87030423,5.5 5.55650904,5.78215388 5.50684662,6.14822944 L5.5,6.25 L5.5,19.754591 C5.5,20.1342868 5.78215388,20.448082 6.14822944,20.4977444 L6.35177056,20.5114376 C6.71784612,20.5611 7,20.8748952 7,21.254591 C7,21.6688046 6.66421356,22.004591 6.25,22.004591 C5.05913601,22.004591 4.08435508,21.0794294 4.00519081,19.9086398 L4,19.754591 L4,6.25 C4,5.05913601 4.92516159,4.08435508 6.09595119,4.00519081 L6.25,4 L8.01344395,3.9994587 C8.13651196,2.8749731 9.08940148,2 10.2466519,2 L13.7533481,2 C14.9105985,2 15.863488,2.8749731 15.9865561,3.9994587 L17.75,4 C18.940864,4 19.9156449,4.92516159 19.9948092,6.09595119 L20,6.25 L20,11.75425 C20,12.1684635 19.6642136,12.50425 19.25,12.50425 C18.8703042,12.50425 18.556509,12.2220961 18.5068466,11.8560205 L18.5,11.75425 L18.5,6.25 C18.5,5.87030423 18.2178461,5.55650904 17.8517706,5.50684662 L17.75,5.5 L15.6182905,5.49983563 C15.214809,6.09910034 14.5301141,6.49330383 13.7533481,6.49330383 Z M16.9999998,13.9975257 L17.9999998,13.9975257 C20.209139,13.9975257 21.9999998,15.7883867 21.9999998,17.9975257 C21.9999998,20.1397211 20.3160315,21.8886046 18.2038265,21.9926208 L18.004591,21.9975151 L17.004591,22.0021276 C16.452312,22.0046417 16.0025461,21.5589866 15.9999891,21.0067076 C15.9976554,20.4938772 16.3817517,20.069438 16.87882,20.0093903 L16.995409,20.0021272 L17.9999998,19.9975257 C19.1045695,19.9975257 19.9999998,19.1020952 19.9999998,17.9975257 C19.9999998,16.9431639 19.1841222,16.0793606 18.1492621,16.0030114 L17.9999998,15.9975257 L16.9999998,15.9975257 C16.4477153,15.9975257 15.9999891,15.5498104 15.9999891,14.9975257 C15.9999891,14.4846898 16.3860402,14.0620185 16.8833787,14.0042534 L16.9999998,13.9975257 L17.9999998,13.9975257 L16.9999998,13.9975257 Z M12.5,14.0021167 L13.5,14.0021167 C14.0522847,14.0021167 14.5,14.4498319 14.5,15.0021167 C14.5,15.5149525 14.1139598,15.9376238 13.6166211,15.9953889 L13.5,16.0021167 L12.5,16.0021167 C11.3954305,16.0021167 10.5,16.8975472 10.5,18.0021167 C10.5,19.0564785 11.3158778,19.9202818 12.3507377,19.9966309 L12.5,20.0021167 L13.5,20.0021167 C14.0522847,20.0021167 14.5,20.4498319 14.5,21.0021167 C14.5,21.5149525 14.1139598,21.9376238 13.6166211,21.9953889 L13.5,22.0021167 L12.5,22.0021167 C10.290861,22.0021167 8.5,20.2112557 8.5,18.0021167 C8.5,15.8599213 10.1839685,14.1110378 12.3003597,14.007012 L12.5,14.0021167 L13.5,14.0021167 L12.5,14.0021167 Z M12.4985614,16.9998212 L18,16.9998212 C18.5522847,16.9998212 19,17.4475364 19,17.9998212 C19,18.512657 18.6139598,18.9353283 18.1166211,18.9930934 L18,18.9998212 L12.4985614,18.9998212 C11.9462767,18.9998212 11.4985614,18.5521059 11.4985614,17.9998212 C11.4985614,17.4869853 11.8846016,17.064314 12.3819403,17.0065489 L12.4985614,16.9998212 L18,16.9998212 L12.4985614,16.9998212 Z M13.7533481,3.5 L10.2466519,3.5 C9.83428745,3.5 9.5,3.83428745 9.5,4.24665191 C9.5,4.65901638 9.83428745,4.99330383 10.2466519,4.99330383 L13.7533481,4.99330383 C14.1657126,4.99330383 14.5,4.65901638 14.5,4.24665191 C14.5,3.83428745 14.1657126,3.5 13.7533481,3.5 Z" id="🎨-Color"></path>
                                </g>
                            </g>
                        </svg>
                        Copy
                    </button>
                </div>

                {copyStatus && (
                    <p className={`text-${copyStatus === 'Failed'? 'red' :'green'}-500 text-sm text-center mb-4`}>{copyStatus}</p>
                )}

                {/* Social Grid */}
                <div className="grid grid-cols-5 gap-4">
                    {items.map((item, i) => (
                        <a
                            key={i}
                            href={item.link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-center"
                        >
                            <div
                                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto hover:scale-110 transition"
                                style={{ background: items?.gradient || item.color }}
                            >
                                {item.icon}
                            </div>
                            <span className="text-[11px] text-white mt-1 block">{item.name}</span>
                        </a>
                    ))}
                </div>
            </dialog>
        </>
    );
}
